import React, { useState, useEffect, useRef } from 'react';
import vcontrolAPI from '../../../services/documentApi';
import { getCachedThumbnail, cacheThumbnail } from '../utils/thumbnailCache';

/**
 * Component to display a floating thumbnail preview of a page.
 * Handles its own data fetching with AbortController to prevent race conditions.
 */
const HoverThumbnailPreview = ({ targetPageLabel, position, pages }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [transform, setTransform] = useState('translate(0, 0)');
  const popupRef = useRef(null);

  // Boundary check to keep popup on screen
  useEffect(() => {
    if (popupRef.current && position && position.clientX !== undefined) {
      const rect = popupRef.current.getBoundingClientRect();
      let tx = 0;
      let ty = 0;
      
      // Calculate how far off-screen it would be
      const predictedRight = position.clientX + 20 + rect.width;
      const predictedBottom = position.clientY + 20 + rect.height;
      
      if (predictedRight > window.innerWidth) {
        // Shift left by width plus padding
        tx = -rect.width - 40;
      }
      if (predictedBottom > window.innerHeight) {
        // Shift up by height plus padding
        ty = -rect.height - 40;
      }
      
      setTransform(`translate(${tx}px, ${ty}px)`);
    }
  }, [position, thumbnailUrl, isLoading]);

  useEffect(() => {
    // If we don't have enough data to fetch, reset state
    if (!targetPageLabel || !pages) {
      setThumbnailUrl(null);
      setIsLoading(false);
      return;
    }

    const targetPageData = pages.find((p) => p.pageLabel === targetPageLabel);
    if (!targetPageData) return;

    // Fast path: Check cache
    const cached = getCachedThumbnail(
      targetPageData.documentId,
      targetPageData.actualPageNumber
    );
    
    if (cached) {
      setThumbnailUrl(cached);
      setIsLoading(false);
      return;
    }

    // Slow path: Fetch with an AbortController
    const abortController = new AbortController();
    setIsLoading(true);

    const fetchThumbnail = async () => {
      try {
        const blob = await vcontrolAPI.getThumbnail(
          targetPageData.documentId,
          targetPageData.actualPageNumber,
          { signal: abortController.signal }
        );

        if (abortController.signal.aborted) return;

        const url = URL.createObjectURL(blob);
        cacheThumbnail(targetPageData.documentId, targetPageData.actualPageNumber, url);
        setThumbnailUrl(url);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Failed to fetch thumbnail for preview:', err);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchThumbnail();

    // Cleanup: Cancel network request if component unmounts or target changes
    return () => {
      abortController.abort();
    };
  }, [targetPageLabel, pages]);

  if (!targetPageLabel || !thumbnailUrl || isLoading) return null;

  return (
    <div
      ref={popupRef}
      className="page-reference-preview-popup"
      style={{ 
        left: `${position.x}px`, 
        top: `${position.y}px`,
        transform 
      }}
    >
      <div className="preview-header">{targetPageLabel}</div>
      <div className="preview-image-container">
        <img src={thumbnailUrl} alt={`Preview of ${targetPageLabel}`} />
      </div>
    </div>
  );
};

export default HoverThumbnailPreview;
